import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import StatusBadge from '../components/StatusBadge';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Search, Download, RefreshCw, Thermometer } from 'lucide-react';
import { cn } from '../lib/utils';

const FILTERS = [
  { key: 'all', label: 'All Devices' },
  { key: 'healthy', label: 'Healthy' },
  { key: 'watchlist', label: 'Watchlist' },
  { key: 'maintenance', label: 'Maintenance Needed' },
  { key: 'critical', label: 'Critical' },
  { key: 'offline', label: 'Offline' },
  { key: 'cool', label: 'Cooling Mode' },
  { key: 'heat', label: 'Heating Mode' },
];

export default function DeviceMonitor() {
  const { devices } = useApp();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');

  useEffect(() => {
    const urlQ = params.get('q');
    if (urlQ) setQ(urlQ);
  }, [params]);

  const filtered = useMemo(() => {
    return devices.filter((d) => {
      if (filter === 'healthy' && d.healthBand !== 'Healthy') return false;
      if (filter === 'watchlist' && d.healthBand !== 'Watchlist') return false;
      if (filter === 'maintenance' && d.healthBand !== 'Maintenance') return false;
      if (filter === 'critical' && d.healthBand !== 'Critical') return false;
      if (filter === 'offline' && d.connection !== 'offline') return false;
      if (filter === 'cool' && d.mode !== 'cool') return false;
      if (filter === 'heat' && d.mode !== 'heat') return false;

      if (q) {
        const query = q.toLowerCase();
        return (
          d.customer.toLowerCase().includes(query) ||
          d.address.toLowerCase().includes(query) ||
          d.id.toLowerCase().includes(query) ||
          d.model.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [devices, filter, q]);

  return (
    <div className="space-y-5" data-testid="devices-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Device Monitor</div>
          <h1 className="mt-1 text-3xl font-semibold text-slate-900 tracking-tight">Connected thermostat fleet</h1>
          <p className="mt-1 text-sm text-slate-500">
            {devices.length} Honeywell devices · live telemetry from customer premises
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="h-9" data-testid="devices-refresh">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button variant="outline" className="h-9" data-testid="devices-export">
            <Download className="h-4 w-4 mr-2" /> Export
          </Button>
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <div className="p-4 border-b border-slate-200 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search customer, address, device ID, model..."
              className="pl-9 h-9 bg-slate-50 border-slate-200 focus-visible:bg-white"
              data-testid="devices-search"
            />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="devices-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`filter-${f.key}`}
                className={cn(
                  'px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
                  filter === f.key
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-[10px] uppercase tracking-wider">Customer</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Address</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Model</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Device ID</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Indoor</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Humidity</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Setpoint</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Mode</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Equipment</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Health</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Last Check-in</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((d) => (
                <TableRow
                  key={d.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => navigate(`/devices/${d.id}`)}
                  data-testid={`device-row-${d.id}`}
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-slate-900">{d.customer}</div>
                      {d.hasActiveAlert && (
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500" title="Active alert" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-slate-600 max-w-[200px] truncate">{d.address}</TableCell>
                  <TableCell className="text-sm text-slate-700">{d.model}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{d.id}</TableCell>
                  <TableCell><StatusBadge status={d.connection === 'offline' ? 'offline' : d.healthBand.toLowerCase()} /></TableCell>
                  <TableCell className="text-right font-mono text-sm">{d.connection === 'offline' ? '—' : `${d.indoorTemp}°`}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{d.connection === 'offline' ? '—' : `${d.humidity}%`}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{d.connection === 'offline' ? '—' : `${d.setpoint}°`}</TableCell>
                  <TableCell><StatusBadge status={d.mode}>{d.mode === 'cool' ? 'Cool' : d.mode === 'heat' ? 'Heat' : d.mode === 'auto' ? 'Auto' : 'Off'}</StatusBadge></TableCell>
                  <TableCell><StatusBadge status={d.equipmentStatus}>{d.equipmentStatus === 'fan' ? 'Fan Only' : d.equipmentStatus === 'aux' ? 'Aux Heat' : d.equipmentStatus.charAt(0).toUpperCase() + d.equipmentStatus.slice(1)}</StatusBadge></TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-2">
                      <div className="w-14 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className={cn('h-full',
                            d.healthScore >= 85 ? 'bg-emerald-500' :
                            d.healthScore >= 70 ? 'bg-blue-500' :
                            d.healthScore >= 60 ? 'bg-amber-500' :
                            d.healthScore > 0 ? 'bg-red-500' : 'bg-slate-400'
                          )}
                          style={{ width: `${d.healthScore}%` }}
                        />
                      </div>
                      <span className="font-mono text-sm font-medium w-7 text-right">{d.healthScore}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{d.lastCheckIn}</TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} className="text-center py-12 text-slate-500">
                    <Thermometer className="h-10 w-10 mx-auto text-slate-300" />
                    <div className="mt-3 text-sm">No devices match the current filter.</div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
          <div data-testid="devices-count">
            Showing <span className="font-mono text-slate-700">{filtered.length}</span> of{' '}
            <span className="font-mono text-slate-700">{devices.length}</span> devices
          </div>
          <div className="font-mono">
            Telemetry refresh: 30s · Honeywell Connected Home API
          </div>
        </div>
      </Card>
    </div>
  );
}
